import React, { useState } from "react";
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Switch, ScrollView, Alert, Modal } from "react-native";
import { observer } from "mobx-react";
import { useRoute, RouteProp } from "@react-navigation/native";
import { desenvolverStore } from "../stores/DesenvolverStore";
import { activityStore } from "../stores/ActivityStore";
import { extraStore } from "../stores/ExtraStore";
import { EnumRoutesAppWeb } from "../enum/EnumRoutesAppWeb";
import { goBack } from "../services/NavigationService";
import { Theme } from "../enum/Theme";
import PhotoUploader from "./PhotoUploader";
import { IContent } from "@interfaces/icontent";
import { IDesenvolvimentoRange } from "@interfaces/idesenvolvimentoRange";
import { AgeRange } from "@comps/AgeBottomSheet";
import { uuidv4 } from "domain/utils/idUtils";
import RichTextEditor from "./RichTextEditor";
import { ageRangeList } from "@enum/AgeRangeList";
import { Language } from "@constants/language";
import { LanguageSelector } from "../comps/LanguageSelector";
import SpaceH from "@comps/SpaceH";

type DesenvolvimentoFormRouteProp = RouteProp<
  { [EnumRoutesAppWeb.createEditDesenvolvimento]: { range?: IDesenvolvimentoRange } },
  EnumRoutesAppWeb.createEditDesenvolvimento
>;

const DesenvolvimentoForm = observer(() => {
  const route = useRoute<DesenvolvimentoFormRouteProp>();
  const range = route.params?.range;

  const existingAgeRange = range?.intro?.ageRanges?.[0] as AgeRange | undefined;
  const [lang, setLang] = useState<Language>(range?.lang ?? Language.PT);
  const [selectedId, setSelectedId] = useState<AgeRange>(existingAgeRange || "RN");
  const [modalIdVisible, setModalIdVisible] = useState(false);

  const [intro, setIntro] = useState<IContent>(
    range?.intro || {
      lang: 'pt',
      id: uuidv4(),
      title: "",
      description: "",
      active: true,
      medias: [],
      ageRanges: [],
    }
  );

  const [atividades, setAtividades] = useState<Record<string, boolean>>(range?.atividades || {});
  const [extras, setExtras] = useState<string[]>(range?.extras || []);
  const [rangeActive, setRangeActive] = useState<boolean>(range?.active !== false);
  const [modalAtividades, setModalAtividades] = useState(false);
  const [modalExtras, setModalExtras] = useState(false);
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Busca local para cada modal
  const [atividadeSearch, setAtividadeSearch] = useState("");
  const [extraSearch, setExtraSearch] = useState("");

  // Filtragem de atividades pelo idioma do form e busca
  const atividadesFiltradas = activityStore.activities.filter((act) => {
    const matchLang = act.lang === lang;
    const matchSearch = atividadeSearch.trim().length === 0
      ? true
      : (act.title?.toLowerCase() || "").includes(atividadeSearch.toLowerCase());
    return matchLang && matchSearch;
  });

  // Filtragem de extras pelo idioma do form e busca
  const extrasFiltrados = extraStore.extras.filter((extra) => {
    const matchLang = extra.lang === lang;
    const matchSearch = extraSearch.trim().length === 0
      ? true
      : (extra.title?.toLowerCase() || "").includes(extraSearch.toLowerCase());
    return matchLang && matchSearch;
  });

  const isFormValid = () =>
    (!!selectedId || !!existingAgeRange) && intro.title.trim();

  const validateForm = () => {
    const errors = [];
    
    if (!intro.title.trim()) {
      errors.push("• Título é obrigatório");
    }
    
    if (!intro.description || !intro.description.trim()) {
      errors.push("• Descrição é obrigatória");
    }
    
    if (!intro.medias || intro.medias.length === 0) {
      errors.push("• Pelo menos 1 imagem é obrigatória");
    }
    
    if (errors.length > 0) {
      setErrorMessage(errors.join("\n"));
      setModalError(true);
      return false;
    }
    
    return true;
  };

  const handleSave = async () => {
    if (!isFormValid()) {
      Alert.alert("Atenção", "Preencha o título e selecione uma faixa etária.");
      return;
    }

    if (!validateForm()) {
      return; // Modal de erro já foi aberto pela função validateForm
    }

    const finalAgeRange = existingAgeRange || selectedId;

    const introWithAgeRange: IContent = {
      ...intro,
      ageRanges: [finalAgeRange],
      active: true, // Sempre ativo
    };

    const rangeToSave: Omit<IDesenvolvimentoRange, "id"> = {
      lang,
      intro: introWithAgeRange,
      atividades,
      extras,
      active: rangeActive, // Salvar o campo active do range
    };

    await desenvolverStore.saveRange(rangeToSave, finalAgeRange);
    goBack();
  };

  return (
    <ScrollView style={styles.container}>
      <Text style={styles.title}>{range ? "Editar Desenvolvimento" : "Novo Desenvolvimento"}</Text>

      {!existingAgeRange && (
        <>
          <Text style={styles.section}>Faixa Etária (Range):</Text>
          <TouchableOpacity onPress={() => setModalIdVisible(true)} style={styles.input}>
            <Text>
              {ageRangeList.find((r) => r.value === selectedId)?.label || "Selecionar faixa etária"}
            </Text>
          </TouchableOpacity>
        </>
      )}

      <Text style={styles.section}>Título:</Text>
      <TextInput
        placeholder="Digite o título"
        value={intro.title}
        onChangeText={(v) => setIntro({ ...intro, title: v })}
        style={styles.input}
      />

      <LanguageSelector value={lang} onChange={setLang} />

      <Text style={styles.section}>Descrição:</Text>
      <RichTextEditor
        initialContent={intro.description || ""}
        onChange={(v) => setIntro({ ...intro, description: v })}
      />

      <Text style={styles.section}>Foto(s):</Text>
      <PhotoUploader
        postId={selectedId || "tempDesenvolvimento"}
        media={intro?.medias ?? []}
        setMedia={(medias) => setIntro({ ...intro, medias })}
        mainMediaUrl={intro.medias?.[0]?.url || ""}
        setMainMediaUrl={() => { }}
      />


      <SpaceH />
      <View style={styles.switchContainer}>
        <Text>Ativo:</Text>
        <Switch value={rangeActive} onValueChange={setRangeActive} />
      </View>

      <SpaceH />
      <TouchableOpacity style={styles.selectButton} onPress={() => setModalAtividades(true)}>
        <Text style={styles.selectButtonText}>Selecionar Atividades</Text>
      </TouchableOpacity>

      <TouchableOpacity style={styles.selectButton} onPress={() => setModalExtras(true)}>
        <Text style={styles.selectButtonText}>Selecionar Extras</Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.saveButton, { backgroundColor: isFormValid() ? Theme.foreground : "#ccc" }]}
        onPress={handleSave}
        disabled={!isFormValid()}
      >
        <Text style={styles.saveButtonText}>Salvar</Text>
      </TouchableOpacity>

      {/* Modal de seleção de Faixa Etária */}
      <Modal visible={modalIdVisible} animationType="slide">
        <ScrollView style={styles.modalContent}>
          <Text style={styles.modalTitle}>Selecionar Faixa Etária</Text>
          {ageRangeList.map((range) => (
            <TouchableOpacity
              key={range.value}
              onPress={() => {
                setSelectedId(range.value as AgeRange);
                setModalIdVisible(false);
              }}
              style={styles.checkboxRow}
            >
              <Text>{selectedId === range.value ? "☑️" : "⬜️"} {range.label}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </Modal>

      {/* Modal de seleção de Atividades com busca */}
      <Modal visible={modalAtividades} animationType="slide">
        <ScrollView style={styles.modalContent}>
          <Text style={styles.modalTitle}>Selecionar Atividades</Text>
          <TextInput
            placeholder="Buscar atividade..."
            value={atividadeSearch}
            onChangeText={setAtividadeSearch}
            style={styles.input}
          />
          {atividadesFiltradas.map((act) => (
            <TouchableOpacity key={act.id} onPress={() => {
              setAtividades((prev) => {
                const newAtividades = { ...prev };
                if (newAtividades[act.id]) {
                  // Se está marcada, remove da lista
                  delete newAtividades[act.id];
                } else {
                  // Se não está marcada, adiciona na lista
                  newAtividades[act.id] = true;
                }
                return newAtividades;
              });
            }} style={styles.checkboxRow}>
              <Text>{atividades[act.id] ? "☑️" : "⬜️"} {act.title}</Text>
            </TouchableOpacity>
          ))}
          {atividadesFiltradas.length === 0 && (
            <Text style={{ color: "#888", marginTop: 16, textAlign: "center" }}>Nenhuma atividade encontrada</Text>
          )}
          <TouchableOpacity style={styles.modalClose} onPress={() => setModalAtividades(false)}>
            <Text style={styles.modalCloseText}>Fechar</Text>
          </TouchableOpacity>
        </ScrollView>
      </Modal>

      {/* Modal de seleção de Extras com busca */}
      <Modal visible={modalExtras} animationType="slide">
        <ScrollView style={styles.modalContent}>
          <Text style={styles.modalTitle}>Selecionar Extras</Text>
          <TextInput
            placeholder="Buscar extra..."
            value={extraSearch}
            onChangeText={setExtraSearch}
            style={styles.input}
          />
          {extrasFiltrados.map((extra) => (
            <TouchableOpacity key={extra.id} onPress={() => {
              setExtras((prev) =>
                prev.includes(extra.id) ? prev.filter((e) => e !== extra.id) : [...prev, extra.id]
              );
            }} style={styles.checkboxRow}>
              <Text>{extras.includes(extra.id) ? "☑️" : "⬜️"} {extra.title}</Text>
            </TouchableOpacity>
          ))}
          {extrasFiltrados.length === 0 && (
            <Text style={{ color: "#888", marginTop: 16, textAlign: "center" }}>Nenhum extra encontrado</Text>
          )}
          <TouchableOpacity style={styles.modalClose} onPress={() => setModalExtras(false)}>
            <Text style={styles.modalCloseText}>Fechar</Text>
          </TouchableOpacity>
        </ScrollView>
      </Modal>

      {/* Modal de Erro de Validação */}
      <Modal visible={modalError} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.modalErrorContent}>
            <Text style={styles.modalErrorTitle}>Campos Obrigatórios</Text>
            <Text style={styles.modalErrorText}>{errorMessage}</Text>
            <TouchableOpacity style={styles.modalClose} onPress={() => setModalError(false)}>
              <Text style={styles.modalCloseText}>Entendi</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
});

export default DesenvolvimentoForm;

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20, backgroundColor: "#F2F2F2" },
  title: { fontSize: 22, fontWeight: "bold", marginBottom: 20 },
  section: { marginTop: 15, fontWeight: "bold" },
  input: { borderWidth: 1, borderColor: "#ccc", borderRadius: 5, padding: 10, marginBottom: 10 },
  switchContainer: { flexDirection: "row", alignItems: "center", marginBottom: 10, gap: 10 },
  selectButton: { padding: 10, borderRadius: 5, backgroundColor: Theme.foreground, marginTop: 10 },
  selectButtonText: { color: "#fff", textAlign: "center" },
  saveButton: { padding: 15, borderRadius: 5, marginTop: 20 },
  saveButtonText: { color: "#fff", textAlign: "center", fontWeight: "bold" },
  modalContent: { flex: 1, padding: 20 },
  modalTitle: { fontSize: 20, fontWeight: "bold", marginBottom: 20 },
  modalClose: { marginTop: 20, padding: 15, borderRadius: 5, backgroundColor: Theme.foreground },
  modalCloseText: { color: "#fff", textAlign: "center", fontWeight: "bold" },
  checkboxRow: { paddingVertical: 8 },
  modalOverlay: { flex: 1, backgroundColor: "rgba(0,0,0,0.5)", justifyContent: "center", alignItems: "center" },
  modalErrorContent: { backgroundColor: "#fff", borderRadius: 10, padding: 20, margin: 20, minWidth: 300 },
  modalErrorTitle: { fontSize: 18, fontWeight: "bold", marginBottom: 15, color: Theme.foreground, textAlign: "center" },
  modalErrorText: { fontSize: 14, color: "#333", lineHeight: 20, marginBottom: 20 },
});
